import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Icon } from '@iconify/react/dist/iconify.js';
import dayjs from 'dayjs';
import { useRetention } from '../RetentionContext';
import { EmptyState, SectionCard, SoftChip } from '../components/ui';
import { RT } from '../theme/retentionTheme';
import { getRetentionTimeline } from '../../../redux/features/retentionSlice';

const ACTOR = {
  CRO: { color: RT.primary, icon: 'solar:user-id-bold' },
  Tutor: { color: '#7C3AED', icon: 'solar:square-academic-cap-bold' },
  Parent: { color: '#0E7C86', icon: 'solar:users-group-rounded-bold' },
  System: { color: RT.muted, icon: 'solar:settings-bold' },
};

// Every stage change, status log row and submission, newest first (§6.1 timeline).
export default function Timeline() {
  const { courseEnrollmentId } = useRetention();
  const dispatch = useDispatch();
  const { timeline } = useSelector((state) => state.retention);

  useEffect(() => {
    dispatch(getRetentionTimeline(courseEnrollmentId));
  }, [dispatch, courseEnrollmentId]);

  const events = Array.isArray(timeline.data) ? timeline.data : [];

  return (
    <SectionCard icon="solar:history-bold-duotone" title="Timeline" action={events.length ? <SoftChip label={events.length} /> : null}>
      {events.length === 0 ? (
        <EmptyState
          icon={timeline.loading ? 'svg-spinners:ring-resize' : 'solar:history-linear'}
          title={timeline.loading ? 'Loading…' : 'No events yet'}
          caption={timeline.loading ? '' : 'GET /retention/:id/timeline'}
        />
      ) : (
        <Stack>
          {events.map((ev, i) => {
            const a = ACTOR[ev.actor_type] || ACTOR.System;
            return (
              <Stack key={i} direction="row" gap={2}>
                <Typography variant="caption" sx={{ width: 104, flexShrink: 0, pt: 1, textAlign: 'right', color: RT.muted, fontWeight: 600 }}>
                  {ev.event_at ? dayjs(ev.event_at).format('DD MMM, HH:mm') : '—'}
                </Typography>
                <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                  <Box sx={{ width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', color: a.color, bgcolor: alpha(a.color, 0.12) }}>
                    <Icon icon={a.icon} width={15} />
                  </Box>
                  {i < events.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: RT.line, my: 0.5 }} />}
                </Stack>
                <Box sx={{ pb: 2, pt: 0.5, minWidth: 0 }}>
                  <Typography variant="subtitle2">{ev.title}</Typography>
                  <Typography variant="caption" sx={{ color: RT.muted }}>
                    {[ev.actor_name, ev.note].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}
    </SectionCard>
  );
}
